// eslint-disable-next-line no-unused-vars
import React, { useCallback } from 'react';
import { bool, array, string, func, object } from 'prop-types';
import { DropdownSkeleton } from '@carbon/react';
import uniqueId from 'lodash/uniqueId';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Check } from 'lucide-react';

import { NWComboBox } from 'Components/shared';

const StyledDropdownSkeleton = styled.div`
  margin: 15px auto 3px;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
  width: calc(100% - 30px);

  p {
    position: absolute;
    left: 15px;
    top: 50%;
    transform: translateY(-50%);
    color: ${ props => props.theme.colors.darkGray };
    font-size: 0.875rem;
  }

  @media screen and (max-width: 1024px) {
    width: 100%
  }
`;

const StyledDropdown = styled(NWComboBox)`
  margin: 15px auto 3px;
  width: calc(100% - 30px);
  border-radius: 6px;
  font-size: 0.875em;

  &.cds--list-box--expanded {
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;

    .cds--list-box__menu {
      padding-top: 5px;
      border: 1px solid ${ props => props.theme.products.colors.addToFavoriteBorder };
      border-top: 1px solid ${ props => props.theme.colors.blue } !important;
    }
  }

  .cds--list-box__field {
    background-color: ${ props => props.theme.products.colors.addToFavorite };
    border: 1px solid ${ props => props.theme.products.colors.addToFavoriteBorder };
    position: relative;
    .cds--text-input {
      &:active, &:focus {
        border: 1px solid ${ props => props.theme.colors.blue } !important;
        border-bottom: 0 !important;
      }
      position: absolute;
      height: calc(100% + 1px);
      top: 0;
      left: 0;
      width: 100%;
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
      outline: 0 none !important;
      border: 0 none !important;
    }
  }

  .cds--list-box__menu-icon {
    &.cds--list-box__menu-icon--open {
      border-left: 0;
      border-radius: 6px 0 6px 0;
      border-right: 1px solid ${ props => props.theme.products.colors.addToFavoriteBorder };
    }

    position: absolute;
    top: 0;
    right: 1px;
    height: calc(100% - 1px);
    border-left: 1px solid ${ props => props.theme.products.colors.addToFavoriteBorder };
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
    background-color: ${ props => props.theme.colors.white };
    width: 35px; //florin was here
  }

  .cds--list-box__menu {
    background-color: ${ props => props.theme.colors.white };
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    box-shadow: none;

    .cds--list-box__menu-item__option {
      p {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 0.875rem;
        color: ${ props => props.theme.colors.darkGray };

        svg {
          position: relative;
          right: 8px;
          color: ${ props => props.theme.colors.gray };
          transform: scale(1.5);
        }
      }
      padding-right: 0;
      padding-left: 0;
    }
  }

  @media screen and (max-width: 1024px) {
    width: 100%
  }
`;

const WishlistsDropdown = props => {
  const {
    lists,
    loadingLists,
    selectedList,
    setWishlist,
    selectedItem,
    showSelectedIcon = true,
    onInputChange,
    onToggleClick,
  } = props;
  const theme = useTheme();
  const { t } = useTranslation();

  const items = lists.reduce((acc, item) => {
    if (item?.id) {
      acc.push({
        id: item.id,
        label: item.name,
      });
    }
    return acc;
  }, []);

  const itemToElement = useCallback(
    item => {
      const { id, label } = item || {};
      const selectedItemId = selectedList?.id;
      return (
        <p>
          { label }
          { showSelectedIcon && id === selectedItemId && (
            <span><Check /></span>
          ) }
        </p>
      );
    },
    [lists, selectedList?.id, showSelectedIcon]
  );

  return loadingLists
    ? (
      <StyledDropdownSkeleton className="cds--list-box__wrapper">
        <DropdownSkeleton />
        <p>{ t('wishlists.loading') }...</p>
      </StyledDropdownSkeleton>
    )
    : (
      <StyledDropdown
        key={ `wishlists-dropdown-${ selectedList?.id || 'none' }` }
        id={ uniqueId('wishlistsSelector') }
        theme={ theme }
        light
        placeholder=""
        items={ items }
        onFocus={ onToggleClick(true) }
        onBlur={ onToggleClick(false) }
        onInputChange={ onInputChange }
        disabled={ loadingLists }
        label={ selectedList?.name || t('wishlist.selectPrompt') }
        initialSelectedItem={ items.find(item => item.id === selectedItem) }
        onChange={ setWishlist }
        itemToElement={ itemToElement }
      />
    );
};

WishlistsDropdown.propTypes = {
  lists: array,
  loadingLists: bool,
  selectedList: object,
  setWishlist: func,
  onToggleClick: func,
  selectedItem: string,
  showSelectedIcon: bool,
  onInputChange: func
};

WishlistsDropdown.displayName = 'WishlistsDropdownComponent';

export default WishlistsDropdown;
